import { SKINS, type SkinId, type SkinPalette } from "./skins";
import {
  CHAMBER_HEIGHT,
  VIEW_HEIGHT,
  VIEW_WIDTH,
  WALL_LEFT,
  WALL_RIGHT,
  type GameEvent,
  type GameSnapshot,
  type PowerupKind,
  type RenderSegment,
} from "../core/constants";
import { eraTransitionAt } from "../core/eras";
import { ENEMY_SPECS, type EnemySpriteId } from "../core/enemies";
import { ENEMY_IMAGE_URLS } from "./enemy-assets";
import { ERA_IMAGE_URLS } from "./era-assets";
import { PATENT_IMAGE_URLS } from "./patent-assets";
const ENEMY_IMAGES = Object.fromEntries(
  (Object.entries(ENEMY_IMAGE_URLS) as [EnemySpriteId, string][]).map(([id, url]) => {
    const image = new Image();
    image.src = url;
    return [id, image];
  }),
) as Record<EnemySpriteId, HTMLImageElement>;
const ERA_IMAGES = Object.fromEntries(
  Object.entries(ERA_IMAGE_URLS).map(([id, url]) => {
    const image = new Image();
    image.src = url;
    return [id, image];
  }),
) as Record<string, HTMLImageElement>;
const PATENT_IMAGES = Object.fromEntries(
  (Object.entries(PATENT_IMAGE_URLS) as [PowerupKind, string][]).map(([kind, url]) => {
    const image = new Image();
    image.src = url;
    return [kind, image];
  }),
) as Record<PowerupKind, HTMLImageElement>;

export interface RenderPreferences {
  skin: SkinId;
  reducedMotion: boolean;
  highContrast: boolean;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
  size: number;
}
interface LostPatent {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  spin: number;
  life: number;
}

// DOM HUD ends below 160 logical px at 360/390/430 viewport widths.
const HUD_BOTTOM = 160;
const ERA_BANNER_TOP = HUD_BOTTOM + 8;
const CHAIN_CENTER_Y = ERA_BANNER_TOP + 46 + 26;
const ERA_BANNER_VISIBLE_SECONDS = 2.5;
const ERA_BANNER_FADE_SECONDS = 0.5;

export class Renderer {
  private readonly context: CanvasRenderingContext2D;
  private readonly particles: Particle[] = [];
  private readonly lostPatents: LostPatent[] = [];
  private shake = 0;
  private preferences: RenderPreferences = { reducedMotion: false, highContrast: false, skin: "heirloom" };
  private eraBannerSeconds = 0;
  private previousEraId: string | undefined;
  private previousRunState: GameSnapshot["state"] | undefined;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("Canvas 2D is unavailable");
    this.context = context;
    canvas.width = VIEW_WIDTH;
    canvas.height = VIEW_HEIGHT;
  }

  setPreferences(preferences: RenderPreferences): void {
    this.preferences = preferences;
    if (preferences.reducedMotion) this.eraBannerSeconds = 0;
    this.canvas.closest("#game-shell")?.classList.toggle("high-contrast", preferences.highContrast);
  }

  consume(events: GameEvent[]): void {
    for (const event of events) {
      if (event.type === "impact") {
        const count = event.material === "core" ? 5 : 3;
        const color = event.material === "core" ? "#d8cba6" : "#b92f24";
        for (let index = 0; index < count; index += 1) {
          const angle = (index / count) * Math.PI * 2 + event.speed * 0.001;
          this.particles.push({
            x: event.x,
            y: event.y,
            vx: Math.cos(angle) * (30 + event.speed * 0.08),
            vy: Math.sin(angle) * (30 + event.speed * 0.08),
            life: 0.35,
            color,
            size: event.material === "core" ? 2 : 4,
          });
        }
        if (!this.preferences.reducedMotion) this.shake = Math.min(4, event.speed / 130);
      } else if (event.type === "piston") {
        for (let index = -4; index <= 4; index += 1) {
          const tangentX = -event.normalY * index * 12;
          const tangentY = event.normalX * index * 12;
          this.particles.push({
            x: event.x,
            y: event.y,
            vx: -event.normalX * 125 + tangentX,
            vy: -event.normalY * 125 + tangentY,
            life: 0.4,
            color: index % 2 === 0 ? "#68e1ef" : "#f0aa24",
            size: 3,
          });
        }
        if (!this.preferences.reducedMotion) this.shake = 2.5;
      } else if (event.type === "hazardBreak" || event.type === "overdrive" || event.type === "patentCollected") {
        const count = event.type === "overdrive" ? 18 : event.type === "patentCollected" ? 8 : 10;
        for (let index = 0; index < count; index += 1) {
          const angle = (index / count) * Math.PI * 2;
          this.particles.push({ x: event.x, y: event.y, vx: Math.cos(angle) * 110, vy: Math.sin(angle) * 110, life: 0.55, color: index % 2 === 0 ? "#68e1ef" : "#f0aa24", size: 3 });
        }
        if (!this.preferences.reducedMotion) this.shake = event.type === "overdrive" ? 3.5 : 2;
      } else if (event.type === "patentLost") {
        const reduced = this.preferences.reducedMotion;
        this.lostPatents.push({ x: event.x, y: event.y, vx: event.directionX * (reduced ? 38 : 125), vy: reduced ? -35 : -115, rotation: 0, spin: reduced ? 0 : event.directionX * 8, life: 0.72 });
        this.shake = reduced ? 0 : 3;
      } else if (event.type === "death") {
        for (let index = 0; index < 18; index += 1) {
          const angle = (index / 18) * Math.PI * 2;
          this.particles.push({
            x: event.x,
            y: event.y,
            vx: Math.cos(angle) * (70 + (index % 4) * 25),
            vy: Math.sin(angle) * (70 + (index % 3) * 20),
            life: 0.8,
            color: "#b92f24",
            size: 5,
          });
        }
        if (!this.preferences.reducedMotion) this.shake = 4;
      }
    }
  }

  draw(snapshot: GameSnapshot, frameSeconds: number): void {
    const ctx = this.context;
    const runStarted = snapshot.state === "playing" &&
      this.previousRunState !== "playing" && this.previousRunState !== "paused";
    const eraChanged = this.previousEraId !== undefined && this.previousEraId !== snapshot.era.id;
    if (runStarted || eraChanged) {
      this.eraBannerSeconds = this.preferences.reducedMotion
        ? 0
        : ERA_BANNER_VISIBLE_SECONDS + ERA_BANNER_FADE_SECONDS;
    }
    this.previousEraId = snapshot.era.id;
    this.previousRunState = snapshot.state;
    const shakeX = this.preferences.reducedMotion ? 0 : Math.sin(snapshot.tick * 2.17) * this.shake;
    const shakeY = this.preferences.reducedMotion ? 0 : Math.cos(snapshot.tick * 1.73) * this.shake;
    this.shake *= 0.82;

    ctx.save();
    this.drawBackdrop(snapshot);
    this.drawEraBanner(snapshot);
    this.eraBannerSeconds = Math.max(0, this.eraBannerSeconds - frameSeconds);
    ctx.translate(shakeX, shakeY - snapshot.cameraY);
    this.drawSterilizer(snapshot);
    this.drawSurfaces(snapshot);
    this.drawPowerups(snapshot);
    this.drawHazards(snapshot);
    this.updateAndDrawLostPatents(frameSeconds);
    this.updateAndDrawParticles(snapshot.cameraY, frameSeconds);
    if (snapshot.state !== "dead") {
      this.drawTomato(snapshot);
      const sievePromptShown = this.drawSievePrompt(snapshot);
      if (!sievePromptShown) this.drawFirstEncounterPrompt(snapshot);
    }

    ctx.restore();
    this.drawVignette(snapshot);
    this.drawSterilizerWarning(snapshot);
    this.drawCombo(snapshot);
  }

  private drawBackdrop(snapshot: GameSnapshot): void {
    const ctx = this.context;
    const transition = eraTransitionAt(snapshot.eraSeed, snapshot.cameraY + VIEW_HEIGHT / 2);
    ctx.fillStyle = this.preferences.highContrast ? "#fff8d8" : transition.era.paper;
    ctx.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
    if (this.preferences.highContrast) return;

    const localTravel = snapshot.cameraY - transition.startChamber * CHAMBER_HEIGHT;
    const parallax = this.preferences.reducedMotion ? 0 : Math.max(-10, Math.min(10, localTravel * -0.018));
    this.drawCoverImage(ERA_IMAGES[transition.era.id], parallax, 1);
    if (transition.progress > 0) this.drawCoverImage(ERA_IMAGES[transition.nextEra.id], parallax * 0.5, transition.progress);

    ctx.save();
    ctx.globalAlpha = 0.32;
    ctx.fillStyle = transition.progress > 0.5 ? transition.nextEra.paper : transition.era.paper;
    ctx.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
    ctx.restore();
  }

  private drawCoverImage(image: HTMLImageElement | undefined, offsetY: number, alpha: number): void {
    if (!image?.complete || image.naturalWidth === 0) return;
    const scale = Math.max(VIEW_WIDTH / image.naturalWidth, VIEW_HEIGHT / image.naturalHeight);
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    this.context.save();
    this.context.globalAlpha = alpha;
    this.context.drawImage(image, (VIEW_WIDTH - width) / 2, (VIEW_HEIGHT - height) / 2 + offsetY, width, height);
    this.context.restore();
  }

  private drawSterilizer(snapshot: GameSnapshot): void {
    const ctx = this.context;
    const bottom = snapshot.sterilizerY;
    ctx.fillStyle = this.preferences.highContrast ? "#000" : "#671f23";
    ctx.fillRect(0, snapshot.cameraY - 120, VIEW_WIDTH, bottom - snapshot.cameraY + 120);
    ctx.fillStyle = "#f0aa24";
    ctx.fillRect(0, bottom - 8, VIEW_WIDTH, 8);
    ctx.fillStyle = "#171b1c";
    for (let x = -20; x < VIEW_WIDTH + 20; x += 24) {
      ctx.beginPath();
      ctx.moveTo(x, bottom - 8);
      ctx.lineTo(x + 12, bottom - 8);
      ctx.lineTo(x + 22, bottom);
      ctx.lineTo(x + 10, bottom);
      ctx.closePath();
      ctx.fill();
    }
  }

  private drawSurfaces(snapshot: GameSnapshot): void {
    // Trunks establish the world-tree silhouette. Branches are painted over them
    // so each flared root visibly grows out of its supporting trunk.
    for (const segment of snapshot.segments) {
      if (segment.id.endsWith(":wall-l") || segment.id.endsWith(":wall-r")) {
        this.drawTimeTrunk(segment);
      }
    }
    for (const segment of snapshot.segments) {
      if (!segment.id.endsWith(":wall-l") && !segment.id.endsWith(":wall-r")) {
        this.drawTimeBranch(segment);
      }
    }
  }

  private drawTimeBranch(segment: RenderSegment): void {
    const ctx = this.context;
    const ax = segment.ax + segment.offsetX;
    const ay = segment.ay + segment.offsetY;
    const bx = segment.bx + segment.offsetX;
    const by = segment.by + segment.offsetY;
    const dx = bx - ax;
    const dy = by - ay;
    const length = Math.hypot(dx, dy);
    if (length === 0) return;

    // Rapier's rounded segment reaches five units from its centerline. The top
    // edge stays straight and exact; taper and curvature live below it.
    const normalDirection = dx >= 0 ? 1 : -1;
    const nx = normalDirection * dy / length;
    const ny = normalDirection * -dx / length;
    const tx = dx / length;
    const ty = dy / length;
    const top = 5;
    const rootDepth = 12;
    const tipDepth = 2.5;
    const rootTopX = ax + nx * top;
    const rootTopY = ay + ny * top;
    const tipTopX = bx + nx * top;
    const tipTopY = by + ny * top;
    const tipBottomX = bx - nx * tipDepth;
    const tipBottomY = by - ny * tipDepth;
    const rootBottomX = ax - nx * rootDepth;
    const rootBottomY = ay - ny * rootDepth;
    const wallX = dx >= 0 ? WALL_LEFT : WALL_RIGHT;
    const wallT = dx === 0 ? 0 : Math.max(0, Math.min(1, (wallX - ax) / dx));
    const junctionX = ax + dx * wallT;
    const junctionY = ay + dy * wallT;
    const branchColor = this.preferences.highContrast ? "#000" : "#55402d";

    ctx.save();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    // A broad growth collar breaks the floating-beam silhouette at the trunk.
    ctx.save();
    ctx.translate(junctionX, junctionY);
    ctx.rotate(Math.atan2(dy, dx));
    ctx.fillStyle = branchColor;
    ctx.beginPath();
    ctx.ellipse(0, 1.5, 15, 6.5, 0, 0, Math.PI * 2);
    ctx.fill();
    if (!this.preferences.highContrast) {
      ctx.strokeStyle = "#bda866";
      ctx.lineWidth = 1.25;
      ctx.beginPath();
      ctx.ellipse(0, 1.5, 10.5, 4.75, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();

    ctx.fillStyle = branchColor;
    ctx.beginPath();
    ctx.moveTo(rootTopX, rootTopY);
    ctx.lineTo(tipTopX, tipTopY);
    ctx.quadraticCurveTo(bx + tx * 4, by + ty * 4, tipBottomX, tipBottomY);
    ctx.bezierCurveTo(
      ax + dx * 0.76 - nx * (tipDepth + 5),
      ay + dy * 0.76 - ny * (tipDepth + 5),
      ax + dx * 0.3 - nx * (rootDepth + 3),
      ay + dy * 0.3 - ny * (rootDepth + 3),
      rootBottomX,
      rootBottomY,
    );
    ctx.closePath();
    ctx.fill();

    if (!this.preferences.highContrast) {
      ctx.strokeStyle = "#56795b";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(junctionX - nx * 3, junctionY - ny * 3);
      ctx.quadraticCurveTo(
        ax + dx * 0.48 - nx * 7,
        ay + dy * 0.48 - ny * 7,
        ax + dx * 0.78 - nx * 3,
        ay + dy * 0.78 - ny * 3,
      );
      ctx.stroke();

      const nodeT = 0.38;
      const nodeX = ax + dx * nodeT - nx * 7;
      const nodeY = ay + dy * nodeT - ny * 7;
      ctx.save();
      ctx.translate(nodeX, nodeY);
      ctx.rotate(Math.atan2(dy, dx));
      ctx.fillStyle = "#3a2b22";
      ctx.strokeStyle = "#bda866";
      ctx.lineWidth = 1.25;
      ctx.beginPath();
      ctx.ellipse(0, 0, 4, 2.75, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    // This is the only emphasized edge: the real upper collider boundary.
    ctx.strokeStyle = this.preferences.highContrast ? "#fff" : "#b8c98d";
    ctx.lineWidth = this.preferences.highContrast ? 2.5 : 2;
    ctx.beginPath();
    ctx.moveTo(rootTopX, rootTopY);
    ctx.lineTo(tipTopX, tipTopY);
    ctx.stroke();
    ctx.restore();
  }

  private drawTimeTrunk(segment: RenderSegment): void {
    const ctx = this.context;
    const x = segment.ax + segment.offsetX;
    const top = Math.min(segment.ay, segment.by) + segment.offsetY;
    const bottom = Math.max(segment.ay, segment.by) + segment.offsetY;
    const isLeft = segment.id.endsWith(":wall-l");
    const outerX = isLeft ? 0 : x;
    const width = isLeft ? x : VIEW_WIDTH - x;
    const inward = isLeft ? 1 : -1;
    const outward = -inward;
    const innerBandX = x + outward * 8;
    const phase = segment.chamber * 0.85;
    const trunkColor = this.preferences.highContrast ? "#000" : "#4b3327";

    ctx.save();
    ctx.fillStyle = trunkColor;
    ctx.fillRect(outerX, top, width, bottom - top);

    // Small contour lobes vary trunk thickness without moving the straight wall.
    for (let index = 0, y = top + 50; y < bottom; index += 1, y += 98) {
      const reach = 3 + ((segment.chamber + index) & 1) * 1.5;
      ctx.beginPath();
      ctx.moveTo(x, y - 12);
      ctx.quadraticCurveTo(x + inward * reach, y - 7, x + inward * reach, y);
      ctx.quadraticCurveTo(x + inward * reach, y + 7, x, y + 12);
      ctx.closePath();
      ctx.fill();
    }

    if (!this.preferences.highContrast) {
      // One broad winding growth strand reads as a vine at mobile scale.
      ctx.strokeStyle = "#3f6048";
      ctx.lineWidth = 7;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(innerBandX + Math.sin(phase) * 2, top - 2);
      ctx.bezierCurveTo(
        innerBandX + Math.sin(phase + 0.8) * 5,
        top + (bottom - top) * 0.32,
        innerBandX + Math.sin(phase + 1.4) * 5,
        top + (bottom - top) * 0.68,
        innerBandX + Math.sin(phase + 2.1) * 2,
        bottom + 2,
      );
      ctx.stroke();

      // Curved cross-bands segment the column like growth rings, not frame joints.
      ctx.strokeStyle = "#708566";
      ctx.lineWidth = 1.25;
      for (let y = top + 72; y < bottom; y += 118) {
        const outerEdge = isLeft ? 1 : VIEW_WIDTH - 1;
        ctx.beginPath();
        ctx.moveTo(outerEdge, y + 2);
        ctx.quadraticCurveTo(x + outward * 10, y - 4, x + outward * 2, y);
        ctx.stroke();
      }

      const knotY = top + 112 + (segment.chamber & 1) * 24;
      if (knotY < bottom - 18) {
        const knotX = x + outward * 8;
        ctx.fillStyle = "#2e241f";
        ctx.strokeStyle = "#bda866";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.ellipse(knotX, knotY, 5, 7.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = "#bda866";
        ctx.beginPath();
        ctx.arc(knotX, knotY, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Continuous and straight even where decorative contour lobes protrude.
    ctx.strokeStyle = this.preferences.highContrast ? "#fff" : "#b8c98d";
    ctx.lineWidth = this.preferences.highContrast ? 3 : 2;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.restore();
  }

  private drawPowerups(snapshot: GameSnapshot): void {
    const ctx = this.context;
    for (const powerup of snapshot.powerups) {
      if (powerup.collected) continue;
      const image = PATENT_IMAGES[powerup.kind];
      if (!image.complete || image.naturalWidth === 0) continue;
      const pulse = this.preferences.reducedMotion ? 1 : 1 + Math.sin(snapshot.tick * 0.12) * 0.08;
      const width = powerup.kind === "gold-patent" ? 48 : 44;
      const height = width * image.naturalHeight / image.naturalWidth;
      ctx.save();
      ctx.translate(powerup.x, powerup.y);
      ctx.scale(pulse, pulse);
      if (this.preferences.highContrast) ctx.filter = "contrast(1.3)";
      ctx.drawImage(image, -width / 2, -height / 2, width, height);
      ctx.restore();
    }
  }


  private updateAndDrawLostPatents(frameSeconds: number): void {
    const image = PATENT_IMAGES["regular-patent"];
    if (!image.complete || image.naturalWidth === 0) return;
    const ctx = this.context;
    for (let index = this.lostPatents.length - 1; index >= 0; index -= 1) {
      const patent = this.lostPatents[index];
      if (!patent) continue;
      patent.life -= frameSeconds;
      if (patent.life <= 0) {
        this.lostPatents.splice(index, 1);
        continue;
      }
      patent.x += patent.vx * frameSeconds;
      patent.y += patent.vy * frameSeconds;
      patent.vy += 210 * frameSeconds;
      patent.rotation += patent.spin * frameSeconds;
      const width = 48;
      const height = width * image.naturalHeight / image.naturalWidth;
      ctx.save();
      ctx.globalAlpha = Math.min(1, patent.life / 0.24);
      ctx.translate(patent.x, patent.y);
      ctx.rotate(patent.rotation);
      ctx.drawImage(image, -width / 2, -height / 2, width, height);
      ctx.restore();
    }
  }
  private drawEnemySprite(hazard: GameSnapshot["hazards"][number]): void {
    const ctx = this.context;
    const x = hazard.renderX;
    const y = hazard.renderY;
    const spec = ENEMY_SPECS[hazard.sprite];
    const image = ENEMY_IMAGES[hazard.sprite];
    if (!image.complete || image.naturalWidth === 0) return;
    ctx.save();
    if (this.preferences.highContrast) ctx.filter = "contrast(1.25)";
    ctx.drawImage(
      image,
      x + (hazard.width - spec.renderWidth) / 2,
      y + (hazard.height - spec.renderHeight) / 2,
      spec.renderWidth,
      spec.renderHeight,
    );
    ctx.restore();
  }

  private drawHazards(snapshot: GameSnapshot): void {
    for (const hazard of snapshot.hazards) {
      this.drawEnemySprite(hazard);
    }
  }

  private drawSievePrompt(snapshot: GameSnapshot): boolean {
    const tomato = snapshot.tomato;
    if (!snapshot.chamberLabel.includes("TIME SIEVE") || tomato.material !== "pulp" ||
        Math.hypot(tomato.vx, tomato.vy) >= 40) return false;
    this.drawPrompt("HOLD!", "SHRINK TO FIT", snapshot);
    return true;
  }

  private drawFirstEncounterPrompt(snapshot: GameSnapshot): void {
    const tomato = snapshot.tomato;
    if (Math.floor(tomato.y / CHAMBER_HEIGHT) > 5 || snapshot.overdriveTicks !== 0) return;
    const hazard = snapshot.hazards
      .filter((candidate) => {
        const distance = candidate.renderY - tomato.y;
        return distance > 0 && distance <= 220;
      })
      .sort((left, right) => left.renderY - right.renderY)[0];
    if (!hazard || hazard.weakness === tomato.material) return;
    this.drawPrompt(
      hazard.weakness === "core" ? "HOLD!" : "LET GO!",
      hazard.weakness === "core" ? "ROBOT SMASHES PRODUCE" : "TOMATO SQUASHES MACHINES",
      snapshot,
    );
  }

  private drawPrompt(title: string, subtitle: string, snapshot: GameSnapshot): void {
    const tomato = snapshot.tomato;
    const ctx = this.context;
    const scale = this.preferences.reducedMotion ? 1 : 1 + 0.07 * Math.sin(snapshot.tick * Math.PI / 30);
    ctx.save();
    ctx.translate(Math.max(84, Math.min(VIEW_WIDTH - 84, tomato.x)), tomato.y - tomato.radius - 47);
    ctx.scale(scale, scale);
    ctx.fillStyle = this.preferences.highContrast ? "#000" : "#241b16";
    ctx.fillRect(-78, -25, 156, 52);
    ctx.strokeStyle = this.preferences.highContrast ? "#fff" : "#fff8d8";
    ctx.lineWidth = 3;
    ctx.strokeRect(-78, -25, 156, 52);
    ctx.textAlign = "center";
    ctx.fillStyle = this.preferences.highContrast ? "#fff" : "#ffe46b";
    ctx.font = "900 29px ui-monospace, monospace";
    ctx.fillText(title, 0, 3);
    ctx.fillStyle = "#fff";
    ctx.font = "800 10px ui-monospace, monospace";
    ctx.fillText(subtitle, 0, 19);
    ctx.restore();
  }

  private drawTomato(snapshot: GameSnapshot): void {
    const { x, y, radius, material, blockedExpansion } = snapshot.tomato;
    const ctx = this.context;
    ctx.save();
    ctx.translate(x, y);
    if (snapshot.overdriveTicks > 0) {
      ctx.strokeStyle = this.preferences.highContrast ? "#000" : "#f0aa24";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(0, 0, radius + 8, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (snapshot.overdriveTicks / 300));
      ctx.stroke();
    }

    if (!this.preferences.reducedMotion) {
      ctx.fillStyle = snapshot.era.accent;
      for (let index = 3; index >= 1; index -= 1) {
        ctx.globalAlpha = 0.06 * index;
        ctx.beginPath();
        ctx.arc(-snapshot.tomato.vx * 0.018 * index, -snapshot.tomato.vy * 0.018 * index, radius * (1 - index * 0.08), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.rotate((snapshot.tomato.x + snapshot.tomato.y * 0.12) / Math.max(1, radius));
    }

    const palette = SKINS.find(skin => skin.id === this.preferences.skin)!.palette;
    drawTomatoBody(ctx, palette, material, blockedExpansion, radius, this.preferences.highContrast);
    ctx.restore();
  }
  private updateAndDrawParticles(cameraY: number, frameSeconds: number): void {
    const ctx = this.context;
    for (let index = this.particles.length - 1; index >= 0; index -= 1) {
      const particle = this.particles[index];
      if (!particle) continue;
      particle.life -= frameSeconds;
      if (particle.life <= 0) {
        this.particles.splice(index, 1);
        continue;
      }
      particle.x += particle.vx * frameSeconds;
      particle.y += particle.vy * frameSeconds;
      particle.vy += 220 * frameSeconds;
      if (particle.y < cameraY - 40 || particle.y > cameraY + VIEW_HEIGHT + 40) continue;
      ctx.globalAlpha = Math.min(1, particle.life * 3);
      ctx.fillStyle = particle.color;
      ctx.fillRect(particle.x - particle.size / 2, particle.y - particle.size / 2, particle.size, particle.size);
    }
    ctx.globalAlpha = 1;
  }


  private drawSterilizerWarning(snapshot: GameSnapshot): void {
    const lead = snapshot.cameraY - snapshot.sterilizerY;
    if (lead <= 0) return;
    const closeness = Math.max(0, Math.min(1, 1 - lead / 760));
    const barHeight = 5 + closeness * 15;
    const ctx = this.context;
    ctx.save();
    ctx.globalAlpha = 0.58 + closeness * 0.34;
    ctx.fillStyle = this.preferences.highContrast ? "#000" : "#671f23";
    ctx.fillRect(0, 0, VIEW_WIDTH, barHeight);
    ctx.fillStyle = "#f0aa24";
    for (let x = 0; x < VIEW_WIDTH; x += 24) {
      ctx.beginPath();
      ctx.moveTo(x, barHeight);
      ctx.lineTo(x + 12, barHeight);
      ctx.lineTo(x + 6, barHeight + 5 + closeness * 5);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }
  private drawCombo(snapshot: GameSnapshot): void {
    if (snapshot.combo <= 0) return;
    const ctx = this.context;
    const pulse = this.preferences.reducedMotion ? 1 : 1 + Math.sin(snapshot.tick * 0.18) * 0.035;
    ctx.save();
    ctx.translate(VIEW_WIDTH / 2, CHAIN_CENTER_Y);
    ctx.scale(pulse, pulse);
    ctx.fillStyle = this.preferences.highContrast ? "#fff" : "rgba(23, 27, 28, 0.88)";
    ctx.strokeStyle = this.preferences.highContrast ? "#000" : "#f0aa24";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(-52, -17, 104, 34, 8);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = this.preferences.highContrast ? "#000" : "#f7edd0";
    ctx.font = "900 14px ui-monospace, monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`CHAIN ×${snapshot.combo}`, 0, 1);
    ctx.restore();
  }

  private drawEraBanner(snapshot: GameSnapshot): void {
    if (this.preferences.reducedMotion || this.eraBannerSeconds <= 0) return;
    const ctx = this.context;
    const fade = this.eraBannerSeconds < ERA_BANNER_FADE_SECONDS
      ? this.eraBannerSeconds / ERA_BANNER_FADE_SECONDS
      : 1;
    ctx.save();
    ctx.globalAlpha = fade;
    ctx.fillStyle = this.preferences.highContrast ? "#000" : snapshot.era.ink;
    ctx.fillRect(56, ERA_BANNER_TOP, VIEW_WIDTH - 112, 46);
    ctx.textAlign = "center";
    ctx.fillStyle = this.preferences.highContrast ? "#fff" : snapshot.era.paper;
    ctx.font = "900 15px ui-monospace, monospace";
    ctx.fillText(snapshot.era.year, VIEW_WIDTH / 2, ERA_BANNER_TOP + 16);
    ctx.font = "900 9px ui-monospace, monospace";
    ctx.fillText(snapshot.era.name, VIEW_WIDTH / 2, ERA_BANNER_TOP + 30);
    ctx.restore();
  }

  private drawVignette(snapshot: GameSnapshot): void {
    const ctx = this.context;
    ctx.save();
    ctx.strokeStyle = this.preferences.highContrast ? "#000" : snapshot.era.ink;
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, VIEW_WIDTH - 6, VIEW_HEIGHT - 6);
    if (snapshot.tomato.blockedExpansion) {
      ctx.textAlign = "center";
      ctx.fillStyle = this.preferences.highContrast ? "#000" : snapshot.era.accent;
      ctx.font = "900 9px ui-monospace, monospace";
      ctx.fillText("NO ROOM TO EXPAND", VIEW_WIDTH / 2, CHAIN_CENTER_Y + 38);
    }
    ctx.restore();
  }
}

// Local origin; callers own translation, presentation rotation, and effects.
export function drawTomatoBody(
  ctx: CanvasRenderingContext2D,
  palette: SkinPalette,
  material: GameSnapshot["tomato"]["material"],
  blockedExpansion: boolean,
  radius: number,
  highContrast: boolean,
): void {
  ctx.save();
  if (material === "core" || blockedExpansion) {
    ctx.fillStyle = highContrast ? "#fff" : palette.plating;
    ctx.strokeStyle = palette.outline;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = palette.flesh;
    ctx.beginPath();
    ctx.arc(0, 0, radius - 4, -Math.PI * 0.8, -Math.PI * 0.2);
    ctx.lineTo(0, 0);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.arc(0, 0, radius - 4, Math.PI * 0.2, Math.PI * 0.8);
    ctx.lineTo(0, 0);
    ctx.closePath();
    ctx.fill();

    for (let index = 0; index < 4; index += 1) {
      ctx.save();
      ctx.rotate(index * Math.PI / 2);
      ctx.fillStyle = palette.trim;
      ctx.fillRect(radius - 2, -4, blockedExpansion ? 12 : 8, 8);
      ctx.fillStyle = palette.accent;
      ctx.fillRect(radius + 1, -2, blockedExpansion ? 8 : 4, 4);
      ctx.restore();
    }

    ctx.fillStyle = "#101719";
    ctx.beginPath();
    ctx.arc(3, -2, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = palette.eye;
    ctx.beginPath();
    ctx.arc(4, -3, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#101719";
    ctx.fillRect(-8, 5, 13, 3);
    ctx.fillStyle = palette.accent;
    ctx.fillRect(-6, 6, 2, 5);
    ctx.fillRect(-1, 6, 2, 5);
    if (blockedExpansion) {
      ctx.strokeStyle = palette.accent;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.arc(0, 0, 20, 0, Math.PI * 2);
      ctx.stroke();
    }
  } else {
    ctx.fillStyle = highContrast ? palette.fleshHighContrast : palette.flesh;
    ctx.strokeStyle = palette.outline;
    ctx.lineWidth = 3;
    ctx.beginPath();
    for (let index = 0; index < 16; index += 1) {
      const angle = index * Math.PI / 8;
      const lobe = radius * (index % 2 === 0 ? 1 : 0.91);
      const px = Math.cos(angle) * lobe;
      const py = Math.sin(angle) * lobe;
      if (index === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    if (palette.stripe) {
      ctx.save();
      // Clip the three curved bands to the exact lobed Pulp body path.
      ctx.clip();
      ctx.strokeStyle = palette.stripe;
      ctx.lineWidth = 3;
      for (const offset of [-12, -5, 2]) {
        ctx.beginPath();
        ctx.moveTo(offset, -radius);
        ctx.bezierCurveTo(offset - 10, -radius / 2, offset - 10, radius / 2, offset, radius);
        ctx.stroke();
      }
      ctx.restore();
    }

    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, radius - 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = highContrast ? "#fff" : palette.plating;
    ctx.fillRect(0, -radius, radius + 2, radius * 2);
    ctx.strokeStyle = palette.trim;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, -radius);
    ctx.lineTo(-3, -7);
    ctx.lineTo(2, 2);
    ctx.lineTo(-2, radius);
    ctx.stroke();
    ctx.restore();

    ctx.fillStyle = palette.leaf;
    ctx.beginPath();
    ctx.moveTo(0, -radius + 3);
    ctx.lineTo(-11, -radius - 6);
    ctx.lineTo(-3, -radius + 5);
    ctx.lineTo(8, -radius - 8);
    ctx.lineTo(5, -radius + 5);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = palette.trim;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(5, -radius + 2);
    ctx.lineTo(11, -radius - 13);
    ctx.stroke();
    ctx.fillStyle = palette.eye;
    ctx.beginPath();
    ctx.arc(12, -radius - 15, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = palette.outline;
    ctx.fillRect(-8, -4, 3, 6);
    ctx.beginPath();
    ctx.arc(7, -2, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = palette.eye;
    ctx.beginPath();
    ctx.arc(8, -3, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = palette.trim;
    ctx.fillRect(-5, 7, 14, 3);
    ctx.fillStyle = palette.accent;
    ctx.fillRect(1, 8, 2, 5);
    ctx.fillRect(6, 8, 2, 5);
    ctx.fillStyle = palette.trim;
    ctx.beginPath();
    ctx.arc(13, -10, 2, 0, Math.PI * 2);
    ctx.arc(13, 10, 2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}
